// Nextsj blog
import React, {  useEffect } from 'react';
import { createClient } from '../../prismicio';
import { Mixpanel } from 'utils/mixpanel';
import AllBlogs from 'containers/SaasModern/Blog/AllBlogs';
import { getPrismicLanguage } from 'utils/getPrismicLanguage';
import Page from 'containers/SaasModern/Page';
import MetaHead from 'common/components/MetaHead';

export default function Blogs({ data, data_main, articleTags,promotion, locale }) {
  useEffect(() => {
    Mixpanel.track('Blog view', {
      'Blog title': 'Blog',
    });
  }, []);
  return (
    <Page locale={locale} promotion={promotion}>
      <MetaHead
        title={data_main?.data?.meta_title}
        description={data_main?.data?.meta_description}
        keywords={data_main?.data?.meta_keywords}
        imageUrl={data_main?.data?.meta_image?.url}
      />
           <AllBlogs
             data={data}
             data_main={data_main}
             articleTags={articleTags}
         />
    </Page>
  );
}
export const getStaticProps = async ({ locale }) => {
  const client = createClient();
  const prismicLanguage = getPrismicLanguage(locale);
  
  try {
    // Safely fetch all data with fallbacks
    let dataPosts = [];
    let dataPostsTags = [];
    let dataAuthors = [];
    let dataCategories = [];
    let data_main = null;

    try {
      dataPosts = await client.getAllByType('article', {
        orderings: {
          field: 'my.article.date',
          direction: 'desc',
        },
        lang: prismicLanguage,
      }) || [];
    } catch (error) {
      console.warn('Articles not found, using empty array as fallback');
    }

    try {
      dataPostsTags = await client.getAllByType('article_tags', {
        lang: prismicLanguage,
      }) || [];
    } catch (error) {
      console.warn('Article tags not found, using empty array as fallback');
    }

    try {
      dataAuthors = await client.getAllByType('author', {
        lang: prismicLanguage,
      }) || [];
    } catch (error) {
      console.warn('Authors not found, using empty array as fallback');
    }

    try {
      dataCategories = await client.getAllByType('category', {
        lang: prismicLanguage,
      }) || [];
    } catch (error) {
      console.warn('Categories not found, using empty array as fallback');
    }

    try {
      data_main = await client.getSingle('blog_list_main_page', {
        lang: prismicLanguage,
      });
    } catch (error) {
      console.warn('Blog main page not found, using null as fallback');
      data_main = { data: null };
    }

    // Process posts data with comprehensive null checks
    const processedData = dataPosts.map((post) => {
      // Ensure post and post.data exist
      if (!post || !post.data) {
        return null;
      }

      // Safely find category
      const category = post.data.category?.id 
        ? dataCategories.find(cat => cat?.id === post.data.category.id)
        : null;

      // Safely find author
      const author = post.data.author?.id 
        ? dataAuthors.find(auth => auth?.id === post.data.author.id)
        : null;

      // Safely process tags with comprehensive null checks
      const tags = (post.data.tags && Array.isArray(post.data.tags)) 
        ? post.data.tags.map(tag => {
            if (!tag || !tag.article_tag?.id) return null;
            const foundTag = dataPostsTags.find(existingTag => 
              existingTag?.id === tag.article_tag.id
            );
            return foundTag ? { 
              id: foundTag.id, 
              name: foundTag.data?.title || 'Untitled' 
            } : null;
          }).filter(Boolean)
        : [];

      // Return enhanced post data that maintains original structure
      // but adds processed fields for easier access
      return {
        ...post, // Keep original Prismic structure
        // Add processed fields for easier access
        processedCategory: category ? {
          name: category.data?.name || 'Uncategorized',
          color: category.data?.color || '#000000'
        } : null,
        processedAuthor: author ? {
          name: author.data?.author_name || 'Anonymous',
          avatar: author.data?.author_image || null,
          role: author.data?.author_role || 'Author'
        } : null,
        processedTags: tags,
        // Ensure data.published_date exists for compatibility
        data: {
          ...post.data,
          published_date: post.data.published_date || post.data.date || new Date().toISOString(),
          // Add article_tags in expected format for filtering
          article_tags: post.data.tags || []
        }
      };
    }).filter(Boolean); // Remove any null entries

    // Safely fetch 'sold' document with error handling
    let sold = null;
    try {
      sold = await client.getSingle('sold', {
        lang: prismicLanguage,
      });
    } catch (error) {
      console.warn('Sold document not found, using null as fallback');
      sold = { data: null };
    }

    return {
      props: {
        data: processedData || [], // Pass the enhanced data that maintains original structure
        data_main: data_main || { data: null },
        articleTags: dataPostsTags || [], // Use articleTags for consistency with component
        authors: dataAuthors || [],
        categories: dataCategories || [],
        locale,
        promotion: sold.data,
      },
    };
  } catch (error) {
    console.error('Error in getStaticProps:', error);
    
    // Return safe fallback data instead of throwing
    return {
      props: {
        data: [],
        data_main: { data: null },
        articleTags: [],
        authors: [],
        categories: [],
        locale,
        promotion: null,
      },
    };
  }
};
